import { useEffect, useMemo, useState } from 'react';
import {
  type AdminUserRow,
  type PickedUser,
  type UserRole,
  usersApi,
} from '../lib/api';

const ROLES: UserRole[] = ['User', 'Manager', 'Admin'];

/**
 * Admin-only screen: list `user_profiles` rows, change roles, soft-delete,
 * and add new users via the Entra people picker (Microsoft Graph read-only).
 *
 * Per MIGRATION_PLAN.md §3.2:
 *   - Add user: pick from enabled tenant users → POST /api/users
 *   - Edit role: PUT /api/users/:id { role }
 *   - Remove: DELETE /api/users/:id (soft-delete, never touches Entra)
 */
export function UserManagement() {
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = () => {
    setError(null);
    usersApi
      .list()
      .then(setRows)
      .catch((err: Error) => setError(err.message));
  };

  useEffect(refresh, []);

  const handleRoleChange = async (id: string, role: UserRole) => {
    try {
      const updated = await usersApi.updateRole(id, role);
      setRows((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this user? They will no longer be able to sign in.')) return;
    try {
      await usersApi.remove(id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  return (
    <div style={s.wrap}>
      <header style={s.header}>
        <h1 style={s.title}>User Management</h1>
        <button type="button" style={s.primaryBtn} onClick={() => setShowAdd(true)}>
          Add User
        </button>
      </header>

      {error ? <div style={s.error}>{error}</div> : null}

      {rows == null ? (
        <div style={s.muted}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={s.muted}>No users yet.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={r.is_active ? undefined : s.inactiveRow}>
                <td style={s.td}>{r.displayName}</td>
                <td style={s.td}>{r.email}</td>
                <td style={s.td}>
                  <select
                    value={r.role}
                    onChange={(e) => handleRoleChange(r.id, e.target.value as UserRole)}
                    style={s.select}
                    disabled={!r.is_active}
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={s.td}>{r.is_active ? 'Active' : 'Disabled'}</td>
                <td style={s.td}>
                  {r.is_active ? (
                    <button type="button" style={s.dangerBtn} onClick={() => handleRemove(r.id)}>
                      Remove
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd ? (
        <AddUserDialog
          existingOids={new Set(rows?.map((r) => r.entra_id))}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

interface AddProps {
  existingOids: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

function AddUserDialog({ existingOids, onClose, onAdded }: AddProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<PickedUser | null>(null);
  const [role, setRole] = useState<UserRole>('User');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Debounce search.
  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => {
      setSearching(true);
      usersApi
        .picker(query)
        .then((r) => setResults(r.filter((u) => !existingOids.has(u.oid))))
        .catch((e: Error) => setErr(e.message))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [query, existingOids]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErr(null);
    try {
      await usersApi.create({
        entraId: selected.oid,
        email: selected.mail ?? selected.userPrincipalName,
        displayName: selected.displayName,
        role,
      });
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Add failed');
      setSubmitting(false);
    }
  };

  const visibleResults = useMemo(() => results.slice(0, 25), [results]);

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={s.modalTitle}>Add User</h2>
        <p style={s.modalNote}>
          Search the Entra directory for an enabled user, then choose a role.
        </p>

        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          placeholder="Name or email…"
          style={s.input}
        />

        {searching ? <div style={s.muted}>Searching…</div> : null}

        {visibleResults.length > 0 ? (
          <ul style={s.results}>
            {visibleResults.map((u) => {
              const active = selected?.oid === u.oid;
              return (
                <li
                  key={u.oid}
                  style={{ ...s.resultRow, ...(active ? s.resultRowActive : {}) }}
                  onClick={() => setSelected(u)}
                >
                  <strong>{u.displayName}</strong>
                  <span style={s.resultEmail}>{u.mail ?? u.userPrincipalName}</span>
                </li>
              );
            })}
          </ul>
        ) : null}

        {selected ? (
          <div style={s.roleRow}>
            <label style={s.label}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              style={s.select}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {err ? <div style={s.error}>{err}</div> : null}

        <div style={s.modalFooter}>
          <button type="button" style={s.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            style={s.primaryBtn}
            disabled={!selected || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Adding…' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: { padding: '2rem', maxWidth: '60rem', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '1.5rem', fontWeight: 600, margin: 0 },
  primaryBtn: {
    padding: '0.5rem 1rem',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  secondaryBtn: {
    padding: '0.5rem 1rem',
    background: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '0.4rem',
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '0.3rem 0.6rem',
    background: '#fff',
    color: '#b91c1c',
    border: '1px solid #fca5a5',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '0.5rem', overflow: 'hidden' },
  th: { textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '0.75rem', borderBottom: '1px solid #f3f4f6' },
  inactiveRow: { opacity: 0.55 },
  select: { padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '0.3rem', background: '#fff' },
  muted: { color: '#6b7280', padding: '1rem 0' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '0.6rem 0.8rem', borderRadius: '0.4rem', marginBottom: '1rem' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#fff', borderRadius: '0.6rem', padding: '1.5rem', width: '32rem', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' },
  modalTitle: { fontSize: '1.2rem', fontWeight: 600, margin: '0 0 0.4rem' },
  modalNote: { color: '#6b7280', fontSize: '0.9rem', margin: '0 0 1rem' },
  input: { width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '0.4rem', fontSize: '1rem', marginBottom: '0.75rem' },
  results: { listStyle: 'none', padding: 0, margin: '0 0 1rem', maxHeight: '15rem', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.4rem' },
  resultRow: { display: 'flex', flexDirection: 'column', padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' },
  resultRowActive: { background: '#eff6ff' },
  resultEmail: { color: '#6b7280', fontSize: '0.85rem' },
  roleRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  label: { fontWeight: 500, color: '#374151' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' },
};
